import { useCallback, useEffect, useState } from 'react';
import {
  api,
  ApiError,
  saveFile,
  type Company,
  type CompanyMonth,
  type CompanyMonthWarning,
  type Obligation,
  type Settlement,
} from '../api';
import type { Dictionary, Locale } from '../i18n';
import { amount, currencyLabel, date, money, period as writtenPeriod } from '../format';
import * as Icon from '../components/Icons';
import { AgingPanel } from '../components/AgingPanel';
import type { Panel } from '../components/Shell';

/**
 * Dokąd prowadzi ostrzeżenie.
 *
 * Ostrzeżenie bez wyjścia zostawia człowieka z pytaniem „to gdzie mam to zrobić?". Każde
 * mówi więc nie tylko, czego brakuje, ale i gdzie się to uzupełnia.
 */
const WARNING_GOES_TO: Record<CompanyMonthWarning, { panel: Panel; label: (t: Dictionary) => string }> = {
  NO_SETTINGS: { panel: 'settings', label: (t) => t.dashboard.fixSettings },
  VAT_EXEMPTION_NEAR_LIMIT: { panel: 'settings', label: (t) => t.dashboard.fixSettings },
  VAT_EXEMPTION_EXCEEDED: { panel: 'settings', label: (t) => t.dashboard.fixSettings },
  OVERDUE_RECEIVABLES: { panel: 'settlements', label: (t) => t.dashboard.goToSettlements },
  OVERDUE_PAYABLES: { panel: 'journal', label: (t) => t.dashboard.goToJournal },
  NO_BOOKKEEPER: { panel: 'access', label: (t) => t.dashboard.goToPeople },
  NO_DOCUMENTS: { panel: 'invoices', label: (t) => t.invoices.new },
  // Zawieszenie wpisuje się w danych firmy, tam więc prowadzi też pytanie „a czy daty są dobre?"
  SUSPENDED: { panel: 'settings', label: (t) => t.dashboard.goToSuspensions },
};

/** Domyślnie poprzedni miesiąc — ten właśnie się rozlicza. */
function previousMonth(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth() - 1, 1)).toISOString().slice(0, 7);
}

function daysBetween(from: Date, to: Date): number {
  const day = 24 * 60 * 60 * 1000;
  return Math.round(
    (Date.UTC(to.getFullYear(), to.getMonth(), to.getDate()) -
      Date.UTC(from.getFullYear(), from.getMonth(), from.getDate())) /
      day,
  );
}

/**
 * Ile czasu zostało do terminu. Liczby dni nie odmieniamy („zostało dni: 12”),
 * bo polski, ukraiński i rosyjski mają po trzy formy liczby mnogiej.
 */
function timeLeft(dueDate: string, t: Dictionary): { text: string; tone: 'st-bad' | 'st-warn' | 'st-draft' } {
  const days = daysBetween(new Date(), new Date(`${dueDate}T00:00:00`));
  if (days < 0) return { text: t.dashboard.overdue, tone: 'st-bad' };
  if (days === 0) return { text: t.dashboard.today, tone: 'st-bad' };
  if (days === 1) return { text: t.dashboard.tomorrow, tone: 'st-bad' };
  return { text: `${t.dashboard.daysLeft} ${days}`, tone: days <= 7 ? 'st-warn' : 'st-draft' };
}

/** Emerytalna, rentowa i wypadkowa razem — tak samo jak na deklaracji ZUS DRA. */
function socialPart(zus: NonNullable<CompanyMonth['zus']>): string {
  return (Number(zus.pension) + Number(zus.disability) + Number(zus.accident)).toFixed(2);
}

/**
 * Kwota przypisana terminowi. Obowiązek przychodzi z serwera bez kwoty — bierzemy ją
 * z tego samego wyliczenia miesiąca, z którego wziął się sam termin.
 */
function obligationAmount(obligation: Obligation, data: CompanyMonth): string | null {
  if (obligation.kind === 'VAT') return data.vatResult !== null && Number(data.vatResult) > 0 ? data.vatResult : '0';
  if (obligation.kind === 'ZUS') return data.zus?.total ?? null;
  if (obligation.kind === 'CIT_ADVANCE') return data.cit?.amountDue ?? null;
  /*
   * Roczne rozliczenie zdrowotnej ma sam termin: jego kwota wychodzi z dochodu całego poprzedniego
   * roku, a nie z tego miesiąca. Wpisanie tu zaliczki PIT dałoby liczbę, która wygląda poprawnie
   * i nie ma nic wspólnego z tym terminem.
   */
  if (obligation.kind === 'HEALTH_ANNUAL') return null;
  return data.pitAdvance?.amount ?? null;
}

/** Ile wychodzi z konta w tym miesiącu: VAT, ZUS i zaliczka razem. */
function totalToPay(data: CompanyMonth): string {
  const vat = data.vatResult !== null && Number(data.vatResult) > 0 ? Number(data.vatResult) : 0;
  const zus = Number(data.zus?.total ?? 0);
  const pit = Number(data.pitAdvance?.amount ?? 0);
  // Spółka nie płaci zaliczki PIT, tylko CIT — jedno albo drugie, nigdy oba
  const cit = Number(data.cit?.amountDue ?? 0);
  return (vat + zus + pit + cit).toFixed(2);
}

export function DashboardPanel({
  company,
  t,
  locale,
  onGo,
}: {
  company: Company;
  t: Dictionary;
  locale: Locale;
  onGo?: (panel: Panel) => void;
}) {
  const [month, setMonth] = useState(previousMonth);
  const [data, setData] = useState<CompanyMonth | null>(null);
  const [receivables, setReceivables] = useState<Settlement[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [draBusy, setDraBusy] = useState(false);
  const [draError, setDraError] = useState<string | null>(null);

  /** Deklaracja ZUS DRA za pokazywany miesiąc — plik do wczytania w eZUS albo w Płatniku. */
  async function downloadDra() {
    setDraBusy(true);
    setDraError(null);
    try {
      saveFile(await api.zusDra(company.id, month));
    } catch (failure) {
      setDraError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setDraBusy(false);
    }
  }

  const load = useCallback(async () => {
    setError(null);
    try {
      // Nieopłacone faktury pokazujemy wierszami, nie liczbą — na pulpicie chodzi o to,
      // na czyje pieniądze się czeka, a nie ile ich jest sztuk
      const [summary, open] = await Promise.all([
        api.companyMonth(company.id, month),
        api.receivables(company.id, true).catch(() => [] as Settlement[]),
      ]);
      setData(summary);
      setReceivables(open);
    } catch (failure) {
      setData(null);
      setError(failure instanceof ApiError ? failure.message : String(failure));
    }
  }, [company.id, month]);

  useEffect(() => {
    void load();
  }, [load]);

  const overdue = receivables.filter((item) => item.daysOverdue > 0);
  const receivablesTotal = receivables.reduce((sum, item) => sum + Number(item.remaining), 0);

  return (
    <>
      <div className="hd">
        <div>
          <h3>{t.dashboard.open}</h3>
          <p>
            {[t.companies.forms[company.legalForm] ?? company.legalForm, company.city]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
        <div className="sp">
          <input
            className="inp w"
            type="month"
            aria-label={t.dashboard.month}
            value={month}
            onChange={(event) => setMonth(event.target.value)}
          />
          {onGo && (
            <button type="button" className="btn" onClick={() => onGo('invoices')}>
              <Icon.Plus />
              {t.invoices.new}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="alert a-bad">
          <Icon.Error />
          <div>
            <b>{error}</b>
          </div>
        </div>
      )}

      {data?.warnings.map((warning) => {
        const goesTo =
          warning === 'OVERDUE_RECEIVABLES' && data?.aging ? WARNING_GOES_TO.OVERDUE_PAYABLES : WARNING_GOES_TO[warning];
        return (
          <div className={warning === 'OVERDUE_RECEIVABLES' || warning === 'OVERDUE_PAYABLES' ? 'alert a-bad' : 'alert a-warn'} key={warning}>
            <Icon.Warn />
            <div>
              <b>{t.dashboard[warning]}</b>
              {warning === 'OVERDUE_RECEIVABLES' && overdue.length > 0 && (
                <p>
                  {t.dashboard.receivablesOverdue}: {overdue.length} · {money(
                    overdue.reduce((sum, item) => sum + Number(item.remaining), 0),
                    locale,
                  )}
                </p>
              )}
            </div>
            {onGo && goesTo && (
              <button type="button" className="btn ghost sm" onClick={() => onGo(goesTo.panel)}>
                {goesTo.label(t)}
              </button>
            )}
          </div>
        );
      })}

      {data && (
        <>
          <div className="split">
            <div className="panel metric">
              <span className="k">
                {t.dashboard.revenue} · {writtenPeriod(month, locale)}
              </span>
              <span className="v num">{money(data.revenue, locale)}</span>
            </div>
            <div className="panel metric">
              <span className="k">
                {t.dashboard.costs} · {writtenPeriod(month, locale)}
              </span>
              <span className="v num">{money(data.costs, locale)}</span>
            </div>
            <div className="panel metric">
              <span className="k">
                {t.dashboard.income} · {writtenPeriod(month, locale)}
              </span>
              <span className="v num">{money(data.income, locale)}</span>
              {data.pitAdvance && (
                <span className="sub">
                  {t.dashboard.advanceBase}: {money(data.pitAdvance.taxBase, locale)}
                </span>
              )}
              {/* Podstawa CIT różni się od wyniku księgowego o koszty niestanowiące kosztów
                  uzyskania i o przychody zwolnione — dlatego pokazujemy ją obok, a nie zamiast */}
              {data.cit && (
                <span className="sub">
                  {t.dashboard.citBase}: {money(data.cit.baseYtd, locale)}
                  {/* Przy zaliczkach kwartalnych kwota dotyczy kwartału, więc podpisujemy ją okresem */}
                  {data.cit.quarterly && ` · ${data.cit.periodLabel}`}
                </span>
              )}
            </div>
            {/*
             * Jedyna karta w kolorze marki: VAT i ZUS wychodzą z konta w tym samym tygodniu,
             * więc stoją zsumowane. Rozbicie jest obok, w panelu terminów.
             */}
            <div className="panel metric lead">
              <span className="k">{t.dashboard.toPay}</span>
              <span className="v num">{money(totalToPay(data), locale)}</span>
              <span className="sub">
                {[
                  data.vatResult !== null && Number(data.vatResult) > 0
                    ? `${t.dashboard.vatToPay} ${money(data.vatResult, locale)}`
                    : null,
                  data.zus ? `${t.dashboard.ZUS} ${money(data.zus.total, locale)}` : null,
                ]
                  .filter(Boolean)
                  .join(' + ')}
              </span>
            </div>
          </div>

          {/* Czego rachunek CIT nie policzył — zdaniami z serwera, nie skrótem */}
          {data.cit && data.cit.notes.length > 0 && (
            <p className="hint">
              {data.cit.notes.map((note) => t.dashboard.citNotes[note] ?? note).join(' ')}
            </p>
          )}

          <div className={data.zus || data.pitAdvance || data.cit || data.vatExemption ? 'grid wider' : 'grid'}>
            <div className="panel">
              <header>
                <h4>{t.dashboard.obligations}</h4>
                <div className="sp">
                  <span className="sm muted">{writtenPeriod(month, locale)}</span>
                </div>
              </header>
              {data.obligations.length === 0 && <p className="muted sm">{t.dashboard.noDeadlines}</p>}
              <div>
                {data.obligations.map((obligation) => {
                  const left = timeLeft(obligation.dueDate, t);
                  const value = obligationAmount(obligation, data);
                  return (
                    <div className="deadline" key={obligation.kind}>
                      <span className={`chip ${left.tone}`}>
                        <i />
                        {left.text}
                      </span>
                      <span className="nm">
                        <b>{t.dashboard[obligation.kind]}</b>
                        <span className="muted xs">
                          {date(obligation.dueDate, locale)}
                          {/* Przesunięcie terminu zaskakuje, więc mówimy wprost, skąd wzięła się data */}
                          {obligation.movedForHoliday &&
                            ` · ${t.dashboard.movedFrom.replace('{date}', date(obligation.statutoryDate, locale))}`}
                        </span>
                      </span>
                      {value !== null && <span className="amt num">{money(value, locale)}</span>}
                    </div>
                  );
                })}
              </div>
              {/* Zastrzeżenia do kwot: skąd się wzięły i czego jeszcze nie liczymy */}
              {data.notes.map((note) => (
                <p className="note" key={note}>
                  {t.dashboard[note]}
                </p>
              ))}
            </div>

            {/*
             * Spółka osobowa: jedna tabela zamiast jednej kwoty. Podatek płaci każdy wspólnik
             * osobno i własną formą, więc kwota „dla firmy" byłaby liczbą, której nikt nie wpłaca.
             */}
            {data.partners.length > 0 && (
              <div className="panel">
                <header>
                  <h4>{t.dashboard.partners}</h4>
                </header>
                <div className="tbl">
                  <table>
                    <thead>
                      <tr>
                        <th>{t.dashboard.partner}</th>
                        <th className="n">{t.dashboard.share}</th>
                        <th>{t.dashboard.taxationForm}</th>
                        <th className="n">{t.dashboard.advanceAmount}</th>
                        <th className="n">{t.dashboard.ZUS}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.partners.map((partner) => (
                        <tr key={partner.partnerId}>
                          <td>{partner.name}</td>
                          <td className="n">{(Number(partner.profitShare) * 100).toFixed(2)}%</td>
                          <td className="muted sm">
                            {t.settings.forms[partner.taxationForm.toLowerCase() as keyof Dictionary['settings']['forms']]}
                          </td>
                          <td className="n mono">
                            {partner.pitAdvance ? money(partner.pitAdvance.amount, locale) : '—'}
                          </td>
                          <td className="n mono">{partner.zus ? money(partner.zus.total, locale) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {(data.zus || data.pitAdvance || data.vatExemption) && (
            <div className="panel">
              {data.zus ? (
                <>
                  <header>
                    <h4>{t.dashboard.zusTitle}</h4>
                  </header>
                  <dl className="brk">
                    <dt>{t.dashboard.zusSocial}</dt>
                    <dd>{money(socialPart(data.zus), locale)}</dd>
                    {data.zus.sickness && (
                      <>
                        <dt>{t.dashboard.zusSickness}</dt>
                        <dd>{money(data.zus.sickness, locale)}</dd>
                      </>
                    )}
                    {data.zus.laborFund && (
                      <>
                        <dt>{t.dashboard.zusLaborFund}</dt>
                        <dd>{money(data.zus.laborFund, locale)}</dd>
                      </>
                    )}
                    <dt>{t.dashboard.zusHealth}</dt>
                    <dd>{money(data.zus.health, locale)}</dd>
                    <dt className="tot">{t.dashboard.zusTotal}</dt>
                    <dd className="tot">{money(data.zus.total, locale)}</dd>
                  </dl>
                  {/*
                    Deklaracja DRA stoi przy kwotach, z których powstaje — a nie w osobnym miejscu,
                    gdzie trzeba by je odszukać drugi raz. Oddajemy plik do wczytania w eZUS albo
                    w Płatniku: kanału do ZUS-u jeszcze nie mamy i mówimy to wprost.
                  */}
                  <div className="row">
                    <button
                      type="button"
                      className="btn sm"
                      disabled={draBusy}
                      onClick={() => void downloadDra()}
                    >
                      {t.dashboard.draDownload}
                    </button>
                    <span className="sm muted">{t.dashboard.draHint}</span>
                  </div>
                  {draError && (
                    <div className="alert a-warn">
                      <Icon.Warn />
                      <div>{draError}</div>
                    </div>
                  )}
                </>
              ) : (
                <header>
                  <h4>{t.dashboard.advanceTitle}</h4>
                </header>
              )}

              {data.pitAdvance && (
                <div className="alert a-info">
                  <Icon.Info />
                  <div>
                    <b>
                      {t.dashboard.advanceTitle}: {money(data.pitAdvance.amount, locale)}
                    </b>
                    <p>
                      {t.dashboard.advanceBase}: {money(data.pitAdvance.taxBase, locale)}
                    </p>
                  </div>
                </div>
              )}

              {data.vatExemption && (
                <dl className="brk">
                  <dt>{t.dashboard.exemptionUsed}</dt>
                  <dd>{money(data.vatExemption.used, locale)}</dd>
                  <dt>{t.dashboard.exemptionLimit}</dt>
                  <dd>{money(data.vatExemption.limit, locale)}</dd>
                  <dt className="tot">{t.dashboard.exemptionRemaining}</dt>
                  <dd className="tot">{money(data.vatExemption.remaining, locale)}</dd>
                </dl>
              )}
            </div>
            )}
          </div>
        </>
      )}

      {/* Pełne księgi: rozrachunki z dziennika zamiast listy nieopłaconych faktur */}
      {data?.aging && <AgingPanel aging={data.aging} t={t} locale={locale} onGo={onGo} />}

      {!data?.aging && receivables.length > 0 && (
        <div className="panel">
          <header>
            <h4>{t.dashboard.receivables}</h4>
            <div className="sp">
              <span className="sm muted">
                {receivables.length} · {money(receivablesTotal, locale)}
                {overdue.length > 0 && ` · ${t.dashboard.receivablesOverdue}: ${overdue.length}`}
              </span>
              {onGo && (
                <button type="button" className="btn ghost sm" onClick={() => onGo('settlements')}>
                  {t.dashboard.goToSettlements}
                </button>
              )}
            </div>
          </header>
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th>{t.invoices.number}</th>
                  <th>{t.settlements.counterparty}</th>
                  <th>{t.invoices.dueDate}</th>
                  <th>{t.invoices.status}</th>
                  <th className="n">
                    {t.invoices.grossAmount} ({currencyLabel(receivables[0]?.currency ?? 'PLN', locale)})
                  </th>
                </tr>
              </thead>
              <tbody>
                {receivables.map((item) => (
                  <tr key={item.documentId}>
                    <td className="doc">{item.documentNumber ?? '—'}</td>
                    <td>{item.counterpartyName ?? '—'}</td>
                    <td className="doc">{date(item.dueDate, locale)}</td>
                    <td>
                      <span className={item.daysOverdue > 0 ? 'chip st-bad' : 'chip st-info'}>
                        <i />
                        {item.daysOverdue > 0
                          ? `${t.settlements.overdueDays} ${item.daysOverdue}`
                          : t.settlements.statuses[item.status]}
                      </span>
                    </td>
                    <td className="n">{amount(item.remaining, locale)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4}>{t.invoices.totals}</td>
                  <td className="n">{amount(receivablesTotal, locale)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
